import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { PortalConfigService } from '../../_services/portal-config.service';

/**
 * Billing & invoicing settings form for a single portal tenant's config
 * (billing_day_of_month, billing_window_days, suspension_grace_period_days,
 * payment_retry_schedule_days). Shared between the System Management
 * "Portal Configuration Editor" (which also has the raw JSON editor) and
 * Portal Management's own Settings tab (scoped to the admin's current
 * portal tenant), so the fields and save logic only exist in one place.
 *
 * Emits saved/saveError rather than showing its own toast, since the two
 * host pages use different notification services (MessageService vs
 * NotificationService) - each parent reports the outcome its own way.
 */
@Component({
  selector: 'share-portal-billing-settings',
  templateUrl: './share-portal-billing-settings.component.html'
})
export class SharePortalBillingSettingsComponent implements OnChanges {
  @Input() portalTenantId: number | null = null;
  @Output() saved = new EventEmitter<void>();
  @Output() saveError = new EventEmitter<any>();

  loading = false;
  saving = false;

  billingDayOfMonth: number | null = null;
  billingWindowDays: number = 7;
  suspensionGracePeriodDays: number | null = null;
  paymentRetryScheduleDaysInput: string = '1,3,7';

  constructor(private pcs: PortalConfigService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['portalTenantId'] && this.portalTenantId) {
      this.loadConfig();
    }
  }

  loadConfig(): void {
    if (!this.portalTenantId) return;

    this.loading = true;
    this.pcs.getConfig(this.portalTenantId).subscribe({
      next: (response: any) => {
        const config = response.body?.config || {};
        this.billingDayOfMonth =
          typeof config.billing_day_of_month === 'number' ? config.billing_day_of_month : null;
        this.billingWindowDays =
          typeof config.billing_window_days === 'number' ? config.billing_window_days : 7;
        this.suspensionGracePeriodDays =
          typeof config.suspension_grace_period_days === 'number'
            ? config.suspension_grace_period_days
            : null;
        this.paymentRetryScheduleDaysInput = Array.isArray(config.payment_retry_schedule_days)
          ? config.payment_retry_schedule_days.join(',')
          : '1,3,7';
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  save(): void {
    if (!this.portalTenantId || this.saving) return;

    const paymentRetryScheduleDays = this.paymentRetryScheduleDaysInput
      .split(',')
      .map((value) => parseInt(value.trim(), 10))
      .filter((value) => !isNaN(value) && value > 0 && value <= 90);

    this.saving = true;
    this.pcs
      .updateConfig(this.portalTenantId, {
        config: {
          billing_day_of_month: this.billingDayOfMonth,
          billing_window_days: this.billingWindowDays,
          suspension_grace_period_days: this.suspensionGracePeriodDays,
          payment_retry_schedule_days:
            paymentRetryScheduleDays.length > 0 ? paymentRetryScheduleDays : [1, 3, 7]
        }
      })
      .subscribe({
        next: () => {
          this.saving = false;
          this.saved.emit();
        },
        error: (error) => {
          this.saving = false;
          this.saveError.emit(error);
        }
      });
  }
}
